import { useEffect } from 'react';
import { numberKeypressFilter } from 'helpers/numberKeypressFilter';

/**
 * `component_selector.js:33` — the rating dialog's number-input character
 * filter, which the chassis port left out (its §30.6 note reuses formKit's
 * `BareInput`, and that carries no keystroke filtering).
 *
 *   'keypress input[data-type="number"], input[data-formatter_type="decimal"]':
 *     'numberKeypress'
 *
 * The binding lives on the component_selector MainView, whose `el` wraps every
 * per-component selector view, so a keypress in the Gas Heater / Coil / Fan /
 * accessory panes bubbles up to it. `#component_rating_mode` (BoardContent
 * §30.1) is that `el` here, so the `closest()` gate reproduces the delegation
 * scope exactly — and, just as deliberately, does NOT reach the AHU board's own
 * tabs, where legacy binds no filter at all (`tab.ahu.js` / `tab.section.js`
 * have no keypress entry) and letters are therefore allowed on dev.
 *
 * `scopeSelector` exists because the header's Independent Rating popup mounts
 * the SAME selectors under a different root (`.component-selector-popup`), and
 * legacy covers it with this one binding: the header host is the same MainView
 * with `options.header = true` (component_selector.js:42-57 branches only on
 * which Dispatcher events it listens to — the `events` hash above it is
 * shared). Without a second mount there, every number field in the popup
 * accepted letters, Armenian included.
 *
 * The selector is legacy's verbatim, note `decimal` but not `integer`: an
 * integer field carrying no `data-type="number"` is unfiltered on dev too.
 *
 * The other four bindings in that hash (`numberFocusValidation`,
 * `numberKeydownValidation`'s arrow spinner, `numberKeyupValidation`'s
 * `.invalid` range class, `numberBlurValidation`'s comma reformat) all write
 * back to the DOM, and the selectors already do their own commit-time
 * formatting; they are not ported here.
 */
export function useRatingNumberKeypress(scopeSelector = '#component_rating_mode'): void {
  useEffect(() => {
    const onKeyPress = (e: KeyboardEvent) => {
      const t = e.target;
      if (!(t instanceof HTMLInputElement)) return;
      if (!t.closest(scopeSelector)) return;
      if (!t.matches('input[data-type="number"], input[data-formatter_type="decimal"]')) return;
      numberKeypressFilter(e, t);
    };
    document.addEventListener('keypress', onKeyPress);
    return () => document.removeEventListener('keypress', onKeyPress);
  }, [scopeSelector]);
}
